import React from 'react';
import { Redirect, Route } from 'react-router-dom';
import { hasAccessToThisRoute } from 'utility/helpers';
import { useSelector } from 'react-redux';
import { ViewMounter } from 'components/view_mounter';
import { Panel, PanelPresets } from 'utility';
import { UserStatus, UserPermissionsIds } from 'utility/enumerations';
import { MountInstructions } from 'redux/actions/global';
import { RootReducerState } from 'redux/reducers';

export const SecureRoute = (props) => {
  const {
    render, exact, path, allowAnonymous, component: Component, pathPermissionId
  } = props;
  const authenticated = useSelector((state: RootReducerState) => state.security.authenticated);
  const deceased = useSelector((state: RootReducerState) => state.security.deceased);
  const currentUser = useSelector((state: RootReducerState) => state.security.user);
  const waiting = useSelector((state: RootReducerState) => state.global.waiting);
  const ssoAuthenticated = useSelector((state: RootReducerState) => state.security.ssoAuthenticated);

  // Start out with passed render or component
  // We will decide whether to override below
  let renderAction = render || ((p) => <Component {...p} />);

  if (!authenticated && !allowAnonymous) {
    // User isn't currently authenticated - redirect them to login route/component instead
    renderAction = () => <Redirect to="/login" />;
  } else if ((['/change-password'].includes(path)) && ssoAuthenticated) {
    renderAction = () => <Redirect to="/" />;
  } else if (currentUser?.passwordExpired
      && !['/change-password'].includes(path)
      && !['/logout'].includes(path)
      && !ssoAuthenticated) {
    // User is authenticated but flagged with 'passwordExpired' so we'll directly mount the change-password component,
    // passing the optional overrideLocation parameter to instruct ViewMounter to update our url on mount.
    // We can't use Redirect like we do when the user isn't authenticated because that would result in an infinite loop
    // (SecureRoute would be called over and over).
    renderAction = (p) => ViewMounter(p, {
      viewPath: 'user/change_password',
      targetPanel: Panel.Primary,
      panelLayout: PanelPresets.OneFull,
      clear: true,
      overrideLocation: '/change-password',
    } as MountInstructions);
  } else if (authenticated
    && !waiting
    && currentUser
    && !(['/change-password', '/logout', '/authorization'].includes(path))
    && currentUser?.requiresSignature) {
    renderAction = (p) => ViewMounter(p, {
      viewPath: 'security/initial_authorization',
      targetPanel: Panel.Primary,
      panelLayout: PanelPresets.OneFull,
      clear: true,
    } as MountInstructions);
  } else if (
    authenticated
    && !waiting
    && currentUser
    && !['/change-password', '/logout', '/training', '/training/slides', '/training/quiz'].includes(path)
    && hasAccessToThisRoute(currentUser?.permissions, UserPermissionsIds.RequiresTraining)
    && (
      (currentUser?.requiresSignature === false && currentUser?.dateLastTraining === null)
      || currentUser?.userStatus.id === UserStatus.PendingTraining
      || currentUser?.userStatus.id === UserStatus.PendingOMMUApproval)) {
    renderAction = (p) => ViewMounter(p, {
      viewPath: 'training/training',
      targetPanel: Panel.Primary,
      panelLayout: PanelPresets.OneFull,
      clear: true,
      overrideLocation: '/training',
    } as MountInstructions);
  } else if (authenticated
    && !waiting
    && currentUser
    && !(['/change-password', '/logout', '/authorization'].includes(path))
    && pathPermissionId
    && (!hasAccessToThisRoute(currentUser?.permissions, pathPermissionId) || deceased)) {
    renderAction = () => <Redirect to="/" />;
  }

  return <Route exact={exact} path={path} render={renderAction} />;
};
